import { useState, useMemo } from "react";
import { useLocation, Link } from "wouter";
import { useTranslation } from "react-i18next";
import { trpc } from "@/utils/trpc";
import { useAuth } from "@/utils/useAuth";
import { Plus, RefreshCw, Search, Trash2, Ticket, CheckCircle2, Clock, Star, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/utils/utils";
import { format } from "date-fns";
import { FioriPageHeader } from "@/components/fiori";

// ============================================================================
// DESIGN TOKENS (matches Permit3 Service Desk mockup)
// ============================================================================

const AVATAR_COLORS = ["#8b5cf6", "#3b82f6", "#10b981", "#f59e0b", "#ec4899", "#06b6d4"];

const PAGE_SIZE = 12;

type TabKey = "all" | "queue" | "reported" | "watching";

// ============================================================================
// HELPERS
// ============================================================================

function avatarColor(name: string) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

function initials(name: string) {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "?";
}

function timeAgo(date: string | Date | null | undefined, t: any) {
  if (!date) return "—";
  const ms = Date.now() - new Date(date).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return t("tickets.justNow");
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return format(new Date(date), "MMM dd");
}

// ============================================================================
// SLA PROGRESS BAR
// ============================================================================

function SlaCell({ ticket, t }: { ticket: any; t: any }) {
  const { status, slaDeadlineAt, createdAt } = ticket;

  if (status === "resolved" || status === "closed") {
    if (!slaDeadlineAt) {
      return <span className="text-[#b8b2c9] text-xs">—</span>;
    }
    const end = new Date(slaDeadlineAt).getTime();
    const closedAt = ticket.closedAt ? new Date(ticket.closedAt).getTime() : Date.now();
    const slaMet = closedAt <= end;

    if (slaMet) {
      return (
        <div className="flex items-center gap-1.5 min-w-0">
          <div className="w-9 flex-shrink-0 h-1.5 rounded-full bg-[#ece9f5] overflow-hidden">
            <div className="h-full rounded-full" style={{ width: "100%", background: "#10b981" }} />
          </div>
          <span className="text-[11.5px] text-[#6b6386] font-medium truncate">{t("tickets.sla.met")} ✓</span>
        </div>
      );
    } else {
      return (
        <div className="flex items-center gap-1.5 min-w-0">
          <div className="w-9 flex-shrink-0 h-1.5 rounded-full bg-[#ece9f5] overflow-hidden">
            <div className="h-full rounded-full" style={{ width: "100%", background: "#ef4444" }} />
          </div>
          <span className="text-[11.5px] text-[#991b1b] font-medium truncate">{t("tickets.sla.breached")}</span>
        </div>
      );
    }
  }
  if (status === "pending") {
    return (
      <div className="flex items-center gap-1.5 min-w-0">
        <div className="w-9 flex-shrink-0 h-1.5 rounded-full bg-[#ece9f5]" />
        <span className="text-[11.5px] text-[#8b84a3] font-medium truncate">{t("tickets.sla.paused")}</span>
      </div>
    );
  }
  if (!slaDeadlineAt) {
    return <span className="text-[#b8b2c9] text-xs">—</span>;
  }

  const start = createdAt ? new Date(createdAt).getTime() : Date.now();
  const end = new Date(slaDeadlineAt).getTime();
  const now = Date.now();
  const total = Math.max(end - start, 1);
  const pct = Math.max(0, Math.min(100, ((now - start) / total) * 100));
  const breached = now >= end;
  const warn = !breached && pct >= 75;
  const fill = breached ? "#ef4444" : warn ? "#f59e0b" : "#10b981";

  const absMs = Math.abs(end - now);
  const hours = Math.floor(absMs / 3_600_000);
  const minutes = Math.floor((absMs % 3_600_000) / 60000);
  const human =
    hours >= 24
      ? `${Math.floor(hours / 24)}d ${hours % 24}h`
      : hours > 0
        ? `${hours}h ${minutes}m`
        : `${minutes}m`;
  const label = breached ? `${t("tickets.sla.breached")} by ${human}` : `${human} remaining`;

  return (
    <div className="flex items-center gap-1.5 min-w-0">
      <div className="w-9 flex-shrink-0 h-1.5 rounded-full bg-[#ece9f5] overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: fill }} />
      </div>
      <span
        className={cn(
          "text-[11.5px] font-medium truncate",
          breached ? "text-[#ef4444] font-semibold" : "text-[#6b6386]",
        )}
        title={label}
      >
        {label}
      </span>
    </div>
  );
}

// ============================================================================
// SMALL UI BITS
// ============================================================================

function StatusBadge({ status, statusConfig }: { status: string; statusConfig: any }) {
  const cfg = statusConfig[status] ?? statusConfig.new;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-bold uppercase tracking-wide whitespace-nowrap"
      style={{ background: cfg.bg, color: cfg.text }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: cfg.dot }} />
      {cfg.label}
    </span>
  );
}

function PriorityTag({ priority, priorityConfig }: { priority: string; priorityConfig: any }) {
  const cfg = priorityConfig[priority] ?? priorityConfig.p3;
  return (
    <span
      className="inline-flex items-center gap-1 text-[11px] font-semibold whitespace-nowrap"
      style={{ color: cfg.color }}
    >
      <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2L2 22h20L12 2z" />
      </svg>
      {cfg.short}
    </span>
  );
}

function Avatar({ name }: { name: string }) {
  return (
    <span
      className="inline-flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-bold text-white flex-shrink-0"
      style={{ background: avatarColor(name) }}
    >
      {initials(name)}
    </span>
  );
}

// ============================================================================
// MAIN
// ============================================================================

export default function AllTickets() {
  const { t } = useTranslation();
  const [, setLocation] = useLocation();
  const { user } = useAuth({});
  const userId = user?.id as number | undefined;

  const [tab, setTab] = useState<TabKey>("all");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [queueFilter, setQueueFilter] = useState("all");
  const [page, setPage] = useState(0);

  const STATUS_CONFIG = useMemo(() => ({
    new: { label: t("tickets.statuses.new"), bg: "#e0e7ff", text: "#3730a3", dot: "#6366f1" },
    assigned: { label: t("tickets.statuses.assigned"), bg: "#dbeafe", text: "#1e40af", dot: "#3b82f6" },
    inProgress: { label: t("tickets.statuses.inProgress"), bg: "#fef3c7", text: "#92400e", dot: "#f59e0b" },
    pending: { label: t("tickets.statuses.pending"), bg: "#fce7f3", text: "#9f1239", dot: "#ec4899" },
    resolved: { label: t("tickets.statuses.resolved"), bg: "#d1fae5", text: "#065f46", dot: "#10b981" },
    closed: { label: t("tickets.statuses.closed"), bg: "#e5e7eb", text: "#374151", dot: "#6b7280" },
  }), [t]);

  const PRIORITY_CONFIG = useMemo(() => ({
    p1: { short: "P1", label: t("tickets.priorities.p1"), color: "#dc2626" },
    p2: { short: "P2", label: t("tickets.priorities.p2"), color: "#ea580c" },
    p3: { short: "P3", label: t("tickets.priorities.p3"), color: "#0891b2" },
    p4: { short: "P4", label: t("tickets.priorities.p4"), color: "#65a30d" },
  }), [t]);

  const TABS = useMemo(() => [
    { key: "all" as TabKey, label: t("tickets.title") },
    { key: "queue" as TabKey, label: t("tickets.myQueue") },
    { key: "reported" as TabKey, label: t("tickets.reportedByMe") },
    { key: "watching" as TabKey, label: t("tickets.watching") },
  ], [t]);

  const { data: queuesData } = trpc.tickets.ticketQueues.getAll.useQuery();
  const queues = queuesData ?? [];
  const queueName = useMemo(() => {
    const m = new Map<number, string>();
    queues.forEach((q: any) => m.set(q.id, q.name));
    return m;
  }, [queues]);

  // Per-tab base filter for the main query
  const tabFilter =
    tab === "queue"
      ? { assigneeId: userId }
      : tab === "reported"
        ? { reporterId: userId }
        : tab === "watching"
          ? { watching: true }
          : {};
  const needsUser = tab === "queue" || tab === "reported";

  const { data, isLoading, isFetching, refetch } = trpc.tickets.getAll.useQuery(
    {
      ...tabFilter,
      status: statusFilter !== "all" ? (statusFilter as any) : undefined,
      priority: priorityFilter !== "all" ? (priorityFilter as any) : undefined,
      queueId: queueFilter !== "all" ? Number(queueFilter) : undefined,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    },
    { enabled: !needsUser || !!userId },
  );

  // Lightweight per-tab counts
  const cAll = trpc.tickets.getAll.useQuery({ limit: 1 });
  const cQueue = trpc.tickets.getAll.useQuery({ assigneeId: userId, limit: 1 }, { enabled: !!userId });
  const cReported = trpc.tickets.getAll.useQuery({ reporterId: userId, limit: 1 }, { enabled: !!userId });
  const cWatching = trpc.tickets.getAll.useQuery({ watching: true, limit: 1 }, { enabled: !!userId });
  const tabCounts: Record<TabKey, number | undefined> = {
    all: cAll.data?.total,
    queue: cQueue.data?.total,
    reported: cReported.data?.total,
    watching: cWatching.data?.total,
  };

  // KPI stats (shared with the Ticketing Dashboard)
  const { data: stats } = trpc.tickets.ticketDashboard.getStats.useQuery();
  const kpis = [
    { label: t("tickets.openTickets"), value: stats?.openCount ?? 0, sub: `${stats?.totalCount ?? 0} ${t("tickets.total")}`, icon: Ticket, accent: "#7c3aed", bg: "#ede9fe" },
    { label: t("tickets.slaCompliance"), value: `${stats?.slaCompliance ?? 0}%`, sub: t("tickets.sla.met"), icon: CheckCircle2, accent: "#0d9488", bg: "#ccfbf1" },
    { label: t("tickets.meanTimeToResolve"), value: `${stats?.mttr ?? 0}h`, sub: t("common.average", "Average"), icon: Clock, accent: "#d97706", bg: "#fef3c7" },
    { label: t("tickets.avgSatisfaction"), value: stats?.csatAvg ? `${stats.csatAvg}/5` : "—", sub: t("common.rating", "Rating"), icon: Star, accent: "#dc2626", bg: "#fee2e2" },
  ];

  const allTickets = data?.tickets ?? [];
  const totalCount = data?.total ?? 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  // Client-side search over the current page
  const tickets = useMemo(() => {
    if (!search.trim()) return allTickets;
    const q = search.toLowerCase();
    return allTickets.filter(
      (t: any) =>
        t.ticketNumber?.toLowerCase().includes(q) ||
        t.title?.toLowerCase().includes(q) ||
        t.reporterName?.toLowerCase().includes(q) ||
        t.assigneeName?.toLowerCase().includes(q),
    );
  }, [allTickets, search]);

  const hasFilters =
    statusFilter !== "all" || priorityFilter !== "all" || queueFilter !== "all" || search.trim() !== "";

  const clearFilters = () => {
    setStatusFilter("all");
    setPriorityFilter("all");
    setQueueFilter("all");
    setSearch("");
    setPage(0);
  };

  const changeTab = (k: TabKey) => {
    setTab(k);
    setPage(0);
  };

  const selectCls =
    "h-9 px-3 pr-8 rounded-lg border border-[#ece9f5] bg-white text-[13px] text-[#3d3554] outline-none cursor-pointer hover:border-[#c4b5fd] focus:border-[#8b5cf6] appearance-none bg-no-repeat bg-[right_0.5rem_center] bg-[length:12px]";
  const chevronBg = {
    backgroundImage:
      "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b6386' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>\")",
  };

  return (
    <div className="-mx-4 md:-mx-6 -mb-4 md:-mb-6 min-h-[calc(100vh-4rem)] bg-white px-4 md:px-6">
      <FioriPageHeader
        title={t("tickets.title")}
        subtitle={`${totalCount.toLocaleString()} ${tab === "all" ? t("tickets.total") : t("tickets.inThisView", "in this view")} · ${tab === "all" ? t("tickets.operationsDashboard") : TABS.find((tabItem) => tabItem.key === tab)?.label}`}
        icon={<Ticket className="h-5 w-5" />}
        onRefresh={() => refetch()}
        isRefreshing={isFetching}
        actions={
          <Link href="/tickets/new">
            <Button size="sm" className="gap-1.5 bg-[#7c3aed] hover:bg-[#6d28d9]">
              <Plus className="h-4 w-4" /> {t("tickets.newTicket")}
            </Button>
          </Link>
        }
      />

      {/* KPI Summary Boxes */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 px-0 pb-4">
        {kpis.map((kpi) => (
          <div
            key={kpi.label}
            className="bg-white border border-[#E0E0E0] rounded-lg px-4 py-3 transition-all hover:border-[#5B2C93]/30"
          >
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ background: kpi.bg }}>
                <kpi.icon className="h-4 w-4" style={{ color: kpi.accent }} />
              </div>
              <div>
                <p className="text-xs text-[#6B6B6B] font-medium">{kpi.label}</p>
                <p className="text-xl font-semibold text-[#2C2C2C]">{kpi.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters bar */}
      <div className="px-0 py-4 flex items-center gap-2.5 flex-wrap">
        {/* Tabs */}
        <div className="flex gap-1 bg-[#f5f3ff] p-[3px] rounded-lg">
          {TABS.map((t) => {
            const active = tab === t.key;
            const count = tabCounts[t.key];
            return (
              <button
                key={t.key}
                onClick={() => changeTab(t.key)}
                className={cn(
                  "flex items-center gap-1.5 px-3.5 py-1.5 rounded-md text-[13px] font-semibold transition-colors",
                  active ? "bg-white text-[#6d28d9] shadow-sm" : "text-[#6b6386] hover:text-[#6d28d9]",
                )}
              >
                {t.label}
                {count !== undefined && (
                  <span
                    className={cn(
                      "px-1.5 rounded-lg text-[11px] font-semibold",
                      active ? "bg-[#7c3aed] text-white" : "bg-[#ede9fe] text-[#6d28d9]",
                    )}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Search — grows to fill space, pushing the filters to the right edge */}
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8b84a3]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("tickets.searchPlaceholder")}
            className="w-full h-9 pl-9 pr-9 rounded-lg border border-[#ece9f5] bg-white text-[13px] text-[#1a1530] outline-none focus:border-[#8b5cf6] focus:shadow-[0_0_0_3px_#ede9fe] transition placeholder:text-[#b8b2c9]"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-5 h-5 rounded text-[#8b84a3] hover:bg-[#f5f3ff] hover:text-[#6d28d9] transition-colors"
              title={t("common.clear")}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Filters — pushed to the right side */}
        <div className="flex items-center gap-2.5 flex-shrink-0">
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
            className={selectCls}
            style={chevronBg}
          >
            <option value="all">{t("tickets.allStatuses")}</option>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>

          <select
            value={priorityFilter}
            onChange={(e) => { setPriorityFilter(e.target.value); setPage(0); }}
            className={selectCls}
            style={chevronBg}
          >
            <option value="all">{t("tickets.allPriorities")}</option>
            {Object.entries(PRIORITY_CONFIG).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>

          <select
            value={queueFilter}
            onChange={(e) => { setQueueFilter(e.target.value); setPage(0); }}
            className={selectCls}
            style={chevronBg}
          >
            <option value="all">{t("tickets.allQueues")}</option>
            {queues.map((q: any) => (
              <option key={q.id} value={String(q.id)}>{q.name}</option>
            ))}
          </select>

          {hasFilters && (
            <button
              onClick={clearFilters}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-[#ece9f5] text-[12px] font-semibold text-[#3d3554] hover:bg-[#f5f3ff] hover:border-[#ddd6fe] hover:text-[#6d28d9] transition-colors"
            >
              <Trash2 className="h-3.5 w-3.5" />
              {t("tickets.clear")}
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="px-0 pb-8 flex-1">
        <div className="bg-white border border-[#ece9f5] rounded-xl overflow-hidden shadow-sm">
          <div>
            <table className="w-full table-fixed border-collapse">
              <colgroup>
                {[
                  { h: "ticketId", w: "12%" },
                  { h: "problemStatement", w: "26%" },
                  { h: "status", w: "11%" },
                  { h: "priority", w: "8%" },
                  { h: "assignee", w: "15%" },
                  { h: "queue", w: "10%" },
                  { h: "sla", w: "12%" },
                  { h: "updated", w: "6%" },
                ].map((c) => (
                  <col key={c.h} style={{ width: c.w }} />
                ))}
              </colgroup>
              <thead>
                <tr className="bg-[#f5f3ff]">
                  {[
                    { key: "ticketId", label: t("tickets.ticketId") },
                    { key: "problemStatement", label: t("tickets.problemStatement") },
                    { key: "status", label: t("tickets.status") },
                    { key: "priority", label: t("tickets.priority") },
                    { key: "assignee", label: t("tickets.assignee") },
                    { key: "queue", label: t("tickets.queue") },
                    { key: "sla", label: t("tickets.sla.slaStatus") },
                    { key: "updated", label: t("tickets.updated") },
                  ].map((h) => (
                    <th
                      key={h.key}
                      className="text-left px-3 py-3 text-[11px] uppercase tracking-wide text-[#6b6386] font-bold border-b border-[#ece9f5] whitespace-nowrap"
                    >
                      {h.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-16 text-center text-[#8b84a3] text-sm">
                      {t("tickets.loadingTickets")}
                    </td>
                  </tr>
                ) : tickets.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-16 text-center">
                      <p className="text-[#3d3554] font-semibold text-sm">{t("tickets.noTicketsFound")}</p>
                      <p className="text-[#8b84a3] text-xs mt-1">
                        {hasFilters
                          ? t("tickets.tryAdjustingSearch")
                          : t("tickets.createFirstTicket")}
                      </p>
                    </td>
                  </tr>
                ) : (
                  tickets.map((ticket: any) => (
                    <tr
                      key={ticket.id}
                      onClick={() => setLocation(`/tickets/${ticket.id}`)}
                      className="cursor-pointer hover:bg-[#f5f3ff] transition-colors border-b border-[#ece9f5] last:border-b-0"
                    >
                      <td className="px-3 py-3.5 overflow-hidden">
                        <span className="font-mono text-[12px] text-[#6d28d9] font-semibold truncate block">
                          {ticket.ticketNumber}
                        </span>
                      </td>
                      <td className="px-3 py-3.5 overflow-hidden">
                        <div className="font-semibold text-[#1a1530] text-[13px] truncate" title={ticket.title}>{ticket.title}</div>
                      </td>
                      <td className="px-3 py-3.5 overflow-hidden">
                        <StatusBadge status={ticket.status} statusConfig={STATUS_CONFIG} />
                      </td>
                      <td className="px-3 py-3.5 overflow-hidden">
                        <PriorityTag priority={ticket.priority} priorityConfig={PRIORITY_CONFIG} />
                      </td>
                      <td className="px-3 py-3.5 overflow-hidden">
                        {ticket.assigneeName ? (
                          <div className="flex items-center gap-2 min-w-0">
                            <Avatar name={ticket.assigneeName} />
                            <span className="text-[13px] text-[#3d3554] truncate" title={ticket.assigneeName}>
                              {ticket.assigneeName}
                            </span>
                          </div>
                        ) : (
                          <span className="text-[13px] text-[#b8b2c9] italic">{t("tickets.unassigned")}</span>
                        )}
                      </td>
                      <td className="px-3 py-3.5 overflow-hidden">
                        {ticket.queueId && queueName.get(ticket.queueId) ? (
                          <span className="inline-flex items-center max-w-full px-2.5 py-1 rounded-xl text-[11px] font-semibold bg-[#ede9fe] text-[#6d28d9] truncate">
                            {queueName.get(ticket.queueId)}
                          </span>
                        ) : (
                          <span className="text-[#b8b2c9] text-xs">—</span>
                        )}
                      </td>
                      <td className="px-3 py-3.5 overflow-hidden">
                        <SlaCell ticket={ticket} t={t} />
                      </td>
                      <td className="px-3 py-3.5 overflow-hidden">
                        <span className="text-[12px] text-[#6b6386] truncate block">
                          {timeAgo(ticket.updatedAt ?? ticket.createdAt, t)}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Footer / pagination */}
          <div className="flex items-center justify-between px-4 py-3 border-t border-[#ece9f5] bg-[#fafaff]">
            <span className="text-[12px] text-[#6b6386]">
              {t("tickets.showingTickets", { count: tickets.length, total: totalCount })}
              {totalPages > 1 ? ` · ${t("common.page", "Page")} ${page + 1} ${t("common.of", "of")} ${totalPages}` : ""}
            </span>
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <button
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-[#ece9f5] text-[12px] font-semibold text-[#3d3554] hover:bg-[#f5f3ff] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  {t("tickets.previous")}
                </button>
                <button
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-[#ece9f5] text-[12px] font-semibold text-[#3d3554] hover:bg-[#f5f3ff] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  {t("tickets.next")}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
